import { useState, Fragment, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Button,
  IconButton,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
  Tab,
  Tabs,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { PropertyMediaDrawer } from '../..';
import CompanyCamPanel from '../../Property/CompanyCamPanel';

import messages from '../../../static/messages';

import propertiesServices from '../../../services/propertiesServices';

import usePagination from '../../../hooks/usePagination';
import useDrawer from '../../../hooks/useDrawer';

import { actions_column, table_container_base } from '../../../static/styles';
import { button } from '../../../static/theme-styles';

const base_media = { name: '', description: '', resource: {} };

const IMAGE_MIME = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'image/gif', 'image/webp', 'image/bmp'];
const VIDEO_MIME = ['video/mp4', 'video/quicktime', 'video/webm', 'video/avi'];
const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic'];
const VIDEO_EXT = ['mp4', 'mov', 'webm', 'avi'];

const isPhotoMedia = (media) => {
  const mime = media?.resource?.mimeType || '';
  if (IMAGE_MIME.includes(mime) || VIDEO_MIME.includes(mime)) return true;
  const ext = media?.name?.toLowerCase().split('.').pop() || '';
  return IMAGE_EXT.includes(ext) || VIDEO_EXT.includes(ext);
};

const isVideoMedia = (media) => {
  const mime = media?.resource?.mimeType || '';
  if (VIDEO_MIME.includes(mime)) return true;
  const ext = media?.name?.toLowerCase().split('.').pop() || '';
  return VIDEO_EXT.includes(ext);
};

const getDocIcon = (media) => {
  const mime = media?.resource?.mimeType || '';
  const ext = media?.name?.toLowerCase().split('.').pop() || '';
  if (mime === 'application/pdf' || ext === 'pdf') return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
  if (mime.includes('excel') || mime.includes('spreadsheet') || ext === 'xls' || ext === 'xlsx' || ext === 'csv')
    return <TableChartIcon sx={{ color: '#2e7d32' }} />;
  if (mime.includes('word') || ext === 'doc' || ext === 'docx')
    return <ArticleIcon sx={{ color: '#1565c0' }} />;
  return <InsertDriveFileIcon sx={{ color: '#757575' }} />;
};

const canPreviewDoc = (media) => {
  const mime = media?.resource?.mimeType || '';
  const ext = media?.name?.toLowerCase().split('.').pop() || '';
  return mime === 'application/pdf' || ext === 'pdf';
};

const extractMediaDate = (media) => {
  const name = media?.name || '';
  const match = name.match(/—\s*([A-Za-z]{3,9}\s+\d{1,2},\s*\d{4})\s*$/);
  if (match) {
    const parsed = moment(match[1], 'MMM D, YYYY');
    if (parsed.isValid()) return parsed.toISOString();
  }
  return media?.resource?.createdAt || null;
};

const getRelativeLabel = (dateKey) => {
  if (dateKey === 'unknown') return 'Unknown date';
  const today = moment().tz('America/Los_Angeles').format('YYYY-MM-DD');
  const yesterday = moment().tz('America/Los_Angeles').subtract(1, 'day').format('YYYY-MM-DD');
  const m = moment(dateKey, 'YYYY-MM-DD');
  if (dateKey === today) return 'Today';
  if (dateKey === yesterday) return 'Yesterday';
  if (m.isSame(moment(), 'week')) return m.format('dddd');
  if (m.isSame(moment(), 'year')) return m.format('MMMM D');
  return m.format('MMMM D, YYYY');
};

const groupByDate = (items) => {
  const withDates = items.map((item) => ({ ...item, _resolvedDate: extractMediaDate(item) }));
  const sorted = withDates.sort((a, b) => new Date(b._resolvedDate || 0) - new Date(a._resolvedDate || 0));
  const groups = [];
  let currentKey = null;
  let currentGroup = null;
  for (const item of sorted) {
    const dateKey = item._resolvedDate ? moment(item._resolvedDate).tz('America/Los_Angeles').format('YYYY-MM-DD') : 'unknown';
    if (dateKey !== currentKey) {
      currentKey = dateKey;
      const fullLabel = dateKey !== 'unknown' ? moment(item._resolvedDate).tz('America/Los_Angeles').format('MMMM D, YYYY') : '';
      currentGroup = { dateKey, label: getRelativeLabel(dateKey), fullDate: fullLabel, items: [] };
      groups.push(currentGroup);
    }
    currentGroup.items.push(item);
  }
  return { groups, flat: sorted };
};

/* ─── Photo Gallery ───────────────────────────────────────── */
const PhotoGallery = ({ items, onEdit, onDelete, onDownload }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const { groups, flat } = useMemo(() => groupByDate(items), [items]);

  const open = lightboxIdx !== null;
  const current = open ? flat[lightboxIdx] : null;

  const prev = () => setLightboxIdx((i) => (i > 0 ? i - 1 : flat.length - 1));
  const next = () => setLightboxIdx((i) => (i < flat.length - 1 ? i + 1 : 0));

  const getFlatIdx = (media) => flat.indexOf(media);

  if (!items.length) return <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No photos or videos</Typography>;

  return (
    <>
      <Box sx={{ position: 'relative', pl: { xs: 3, md: 4 } }}>
        {/* Timeline vertical line */}
        <Box sx={{ position: 'absolute', left: { xs: 10, md: 14 }, top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0' }} />

        {groups.map((group, gi) => (
          <Box key={group.dateKey} sx={{ position: 'relative', mb: 3 }}>
            {/* Timeline dot + date label */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, position: 'relative' }}>
              <Box sx={{
                position: 'absolute',
                left: { xs: -21, md: -26 },
                width: 24, height: 24,
                borderRadius: '50%',
                bgcolor: gi === 0 ? 'primary.main' : '#bdbdbd',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1,
                boxShadow: gi === 0 ? '0 0 0 3px rgba(25,118,210,0.2)' : 'none',
              }}>
                <CalendarTodayIcon sx={{ fontSize: 13, color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: gi === 0 ? 'primary.main' : 'text.primary', lineHeight: 1.2 }}>
                  {group.label}
                </Typography>
                {group.fullDate && group.label !== group.fullDate && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {group.fullDate}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: group.fullDate && group.label !== group.fullDate ? 1 : 0 }}>
                  — {group.items.length} file{group.items.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            {/* Photo grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
              {group.items.map((media) => (
                <Box
                  key={media.id}
                  sx={{
                    position: 'relative',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    bgcolor: '#f0f0f0',
                    height: 120,
                    '&:hover .media-overlay': { opacity: 1 },
                  }}
                  onClick={() => setLightboxIdx(getFlatIdx(media))}
                >
                  {isVideoMedia(media) ? (
                    <video src={media.resource?.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                  ) : (
                    <img
                      src={media.resource?.url}
                      alt={media.name}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}
                  <Box
                    className="media-overlay"
                    sx={{
                      position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                      opacity: 0, transition: 'opacity 0.2s',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {media.name}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={(e) => { e.stopPropagation(); onDownload(media.resource?.url); }}>
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={(e) => { e.stopPropagation(); onEdit(media.id); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={(e) => { e.stopPropagation(); onDelete(media.id); }}>
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Lightbox */}
      <Dialog open={open} onClose={() => setLightboxIdx(null)} maxWidth={false} PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', maxWidth: '95vw', maxHeight: '95vh' } }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={() => setLightboxIdx(null)} sx={{ position: 'absolute', top: -40, right: -8, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
            <CloseIcon />
          </IconButton>

          {flat.length > 1 && (
            <IconButton onClick={prev} sx={{ position: 'absolute', left: -50, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
              <NavigateBeforeIcon fontSize="large" />
            </IconButton>
          )}

          {current && (
            isVideoMedia(current) ? (
              <video src={current.resource?.url} controls style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }} />
            ) : (
              <img src={current.resource?.url} alt={current.name} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, display: 'block' }} />
            )
          )}

          {flat.length > 1 && (
            <IconButton onClick={next} sx={{ position: 'absolute', right: -50, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
              <NavigateNextIcon fontSize="large" />
            </IconButton>
          )}
        </Box>
        {current && (
          <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', mt: 1 }}>
            {current.name} — {lightboxIdx + 1} / {flat.length}
          </Typography>
        )}
      </Dialog>
    </>
  );
};

/* ─── Doc List ────────────────────────────────────────────── */
const DocList = ({ items, isMobile, onEdit, onDelete, onDownload }) => {
  const [previewMedia, setPreviewMedia] = useState(null);
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  const paged = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!items.length) return <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No documents</Typography>;

  return (
    <>
      {!isMobile ? (
        <TableContainer component={Paper} sx={table_container_base}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width="5%" />
                <TableCell width="20%">File Name</TableCell>
                <TableCell width="35%">Description</TableCell>
                <TableCell width="15%">Uploaded by</TableCell>
                <TableCell width="15%">Uploaded at</TableCell>
                <TableCell width="10%">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((media) => (
                <TableRow
                  key={media.id}
                  hover
                  sx={{ cursor: canPreviewDoc(media) ? 'pointer' : 'default' }}
                  onClick={() => canPreviewDoc(media) && setPreviewMedia(media)}
                >
                  <TableCell>{getDocIcon(media)}</TableCell>
                  <TableCell>{media.name}</TableCell>
                  <TableCell>{media.description}</TableCell>
                  <TableCell>{media.resource?.createdBy?.fullName}</TableCell>
                  <TableCell>{media.resource?.createdAt ? moment(media.resource.createdAt).tz('America/Los_Angeles').format('MMM Do YYYY') : '-'}</TableCell>
                  <TableCell sx={actions_column}>
                    <Tooltip title="Download"><IconButton onClick={(e) => { e.stopPropagation(); onDownload(media.resource?.url); }}><FileDownloadIcon /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton onClick={(e) => { e.stopPropagation(); onEdit(media.id); }}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Remove"><IconButton onClick={(e) => { e.stopPropagation(); onDelete(media.id); }}><DeleteForeverIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          {paged.map((media) => (
            <Card
              key={media.id}
              variant="outlined"
              sx={{ mb: 1.5, cursor: canPreviewDoc(media) ? 'pointer' : 'default' }}
              onClick={() => canPreviewDoc(media) && setPreviewMedia(media)}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {getDocIcon(media)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {media.name}
                    </Typography>
                    {media.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>{media.description}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {media.resource?.createdBy?.fullName} • {media.resource?.createdAt ? moment(media.resource.createdAt).tz('America/Los_Angeles').format('MM/DD/YY') : ''}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload(media.resource?.url); }}><FileDownloadIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(media.id); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(media.id); }}><DeleteForeverIcon fontSize="small" /></IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {items.length > 10 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={items.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {/* PDF Preview Modal */}
      <Dialog open={!!previewMedia} onClose={() => setPreviewMedia(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewMedia?.name}
          <Box>
            <IconButton onClick={() => onDownload(previewMedia?.resource?.url)} sx={{ mr: 1 }}>
              <FileDownloadIcon />
            </IconButton>
            <IconButton onClick={() => setPreviewMedia(null)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '80vh' }}>
          {previewMedia && (
            <iframe
              src={previewMedia.resource?.url}
              title={previewMedia.name}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ─── Main Component ──────────────────────────────────────── */
const PropertyMediaTable = ({ mediaList = [], id, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [currentItem, setCurrentItem] = useState(null);
  const [isNewItem, setDrawerMode] = useState(false);

  const confirm = useConfirm();
  const { drawer, openDrawer, foolCheck } = useDrawer(currentItem);

  const photos = useMemo(() => mediaList.filter(isPhotoMedia), [mediaList]);
  const docs = useMemo(() => mediaList.filter((m) => !isPhotoMedia(m)), [mediaList]);

  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();
    if (confirmed === true) { setDrawerMode(false); setCurrentItem(null); resetErrors && resetErrors(); }
  };

  const onAddMedia = () => { setCurrentItem(base_media); setDrawerMode(true); openDrawer(); };

  const onEditMedia = (mediaId) => {
    const media = mediaList.filter((el) => el.id === mediaId)[0];
    delete media.createdAt; delete media.deletedAt; delete media.updatedAt;
    setCurrentItem(media);
    openDrawer();
  };

  const handleDelete = (mediaId) => {
    confirm({ title: messages.confirm_message.remove, description: messages.confirm_message.remove_description('Media') })
      .then(() => removeMedia(mediaId))
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleDownload = (url) => { const link = document.createElement('a'); link.href = url; link.target = '_blank'; link.click(); };

  const addNewMedia = async (resetFile) => {
    delete currentItem.isEdited;
    const updatedMedia = { media: [...mediaList, currentItem] };
    try {
      const response = await propertiesServices.update(id, updatedMedia);
      if (response.status === 200) { onCloseDrawer(); toast.success(messages.property.update); setCurrentItem(null); refresh(); resetFile(); }
    } catch (error) { toast.error(messages.errors.error_try_again); console.log(error); }
  };

  const removeMedia = async (mediaId) => {
    const updatedMedia = { media: [...mediaList.filter((el) => el.id !== mediaId)] };
    try {
      const response = await propertiesServices.update(id, updatedMedia);
      if (response.status === 200) { toast.success(messages.property.update); refresh(); }
    } catch (error) { toast.error(messages.errors.error_try_again); console.log(error); }
  };

  const updateMedia = async () => {
    delete currentItem.isEdited;
    const updatedMedia = { media: [...mediaList.filter((el) => el.id !== currentItem.id), currentItem] };
    try {
      const response = await propertiesServices.update(id, updatedMedia);
      if (response.status === 200) { onCloseDrawer(); toast.success(messages.property.update); setCurrentItem(null); refresh(); }
    } catch (error) { toast.error(messages.errors.error_try_again); console.log(error); }
  };

  return (
    <Fragment>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
        >
          <Tab icon={<PhotoLibraryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`Photos (${photos.length})`} />
          <Tab icon={<DescriptionIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`Documents (${docs.length})`} />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-end' : 'flex-end' }}>
          <CompanyCamPanel propertyId={id} refresh={refresh} />
          <Button variant="contained" onClick={onAddMedia} endIcon={<AddIcon />} sx={{ ...button('secondary') }}>
            Add Media
          </Button>
        </Box>
      </Box>

      {tab === 0 && (
        <PhotoGallery items={photos} onEdit={onEditMedia} onDelete={handleDelete} onDownload={handleDownload} />
      )}

      {tab === 1 && (
        <DocList items={docs} isMobile={isMobile} onEdit={onEditMedia} onDelete={handleDelete} onDownload={handleDownload} />
      )}

      <PropertyMediaDrawer drawer={drawer} onCloseDrawer={onCloseDrawer} isNewItem={isNewItem} currentItem={currentItem} setCurrentItem={setCurrentItem} addNewMedia={addNewMedia} updateMedia={updateMedia} />
    </Fragment>
  );
};

export default PropertyMediaTable;
